import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LoginModal from './LoginModal';
import useUnreadMessages from '../hooks/useUnreadMessages';
import HabboImage from '../components/HabboImage';
import { useFeatureFlags } from '../data/featureFlags';



function FullNavigator({ userData, activeSection, setActiveSection, activePath, setActivePath, fontScale = 1, onFontScaleUp, onFontScaleDown, fontScaleMin = 1.0, fontScaleMax = 1.6 }) {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

    const featureFlags = useFeatureFlags();
    const isAdmin = userData?.hasAdminRole === true;

    const unreadCount = useUnreadMessages(userData);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);

    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [userSearchLoading, setUserSearchLoading] = useState(false);
    const userSearchRef = useRef(null);
    const searchDebounceRef = useRef(null);
    const searchAbortRef = useRef(null);

    const navigate = useNavigate();
    const location = useLocation();

    const CACHE_TTL = 30 * 60 * 1000;

    const readSessionJson = useCallback((key) => {
        try {
            const raw = sessionStorage.getItem(key);
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            if (Date.now() - parsed.ts > CACHE_TTL) return null;
            return parsed.data;
        } catch { return null; }
    }, []);

    const writeSessionJson = useCallback((key, data) => {
        try { sessionStorage.setItem(key, JSON.stringify({ data, ts: Date.now() })); } catch {}
    }, []);







    useEffect(() => {
        if (activeSection !== 'members') {
            clearTimeout(searchDebounceRef.current);
            if (searchAbortRef.current) searchAbortRef.current.abort();
            setUserSearchQuery('');
            setUserSearchResults([]);
            setUserSearchLoading(false);
        }
    }, [activeSection]);

    useEffect(() => {
        if (!userSearchResults.length && !userSearchLoading) return;
        const handleClick = (e) => {
            if (userSearchRef.current && !userSearchRef.current.contains(e.target)) {
                setUserSearchResults([]);
                setUserSearchQuery('');
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [userSearchResults, userSearchLoading]);

    const handleUserSearch = useCallback((e) => {
        const val = e.target.value;
        setUserSearchQuery(val);
        setUserSearchResults([]);
        clearTimeout(searchDebounceRef.current);

        if (val.trim().length < 3) {
            setUserSearchLoading(false);
            return;
        }

        setUserSearchLoading(true);
        searchDebounceRef.current = setTimeout(async () => {
            if (searchAbortRef.current) searchAbortRef.current.abort();
            searchAbortRef.current = new AbortController();
            try {
                const { searchUsers } = await import('../services/api');
                const data = await searchUsers(val.trim(), searchAbortRef.current.signal);
                if (data?.success) setUserSearchResults(data.users || []);
            } catch (err) {
                if (err.name !== 'AbortError') setUserSearchResults([]);
            } finally {
                setUserSearchLoading(false);
            }
        }, 350);
    }, []);

    const handleUserSearchSelect = useCallback((habboName) => {
        clearTimeout(searchDebounceRef.current);
        setUserSearchQuery('');
        setUserSearchResults([]);
        navigate(`/members/profile/${encodeURIComponent(habboName)}`);
    }, [navigate]);

    useEffect(() => {
        if (!userMenuOpen) return;
        const handleClick = (e) => {
            if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
                setUserMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [userMenuOpen]);

    useEffect(() => {
        if (!isMenuOpen) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, [isMenuOpen]);

    const navItems = useMemo(() => {
        const items = [
            {
                key: 'home',
                label: 'home',
                icon: `${process.env.PUBLIC_URL}/img/home.gif`,
                url: '/',
                children: []
            },
            {
                key: 'news',
                label: 'news & guides',
                icon: `${process.env.PUBLIC_URL}/img/icons/icon_264.png`,
                section: 'news',
                children: [
                    { path: '/articles/all', label: 'All Articles' },
                    { path: '/observer', label: 'The Origins Observer', feature: 'observer' },
                    { path: '/articles/franklynews', label: 'Frankly News' },
                    { path: '/articles/habbonews', label: 'Habbo News' },
                    { path: '/articles/franklyguides', label: 'Frankly Guides' },
                    { path: '/articles/guides', label: 'Habbo Guides' },
                ]
            },
            {
                key: 'values',
                label: 'values',
                icon: `${process.env.PUBLIC_URL}/img/values.gif`,
                url: '/values',
                //section: 'values',
                children: [
                    //{ path: '/values', label: 'Furni Values' }
                ]
            },
            {
                key: 'events',
                label: 'events',
                icon: `${process.env.PUBLIC_URL}/img/icons/icon_346.png`,
                url: '/events',
                children: []
            },
            {
                key: 'goodies',
                label: 'goodies',
                icon: `${process.env.PUBLIC_URL}/img/icons/icon_258.png`,
                section: 'goodies',
                children: [
                    { path: '/goodies/imager', label: userData ? 'Habbo ProImager' : 'Habbo Imager', feature: 'imager' },
                    { path: '/goodies/fontgenerator', label: 'Font Generator', feature: 'fontgenerator' },
                    { path: '/goodies/furnicolourer', label: 'Furni Colourer', feature: 'furnicolourer' },
                    { path: '/goodies/franklywheel', label: 'Frankly Wheel', feature: 'franklywheel' },
                    { path: '/goodies/appreciate', label: 'I Appreciate You', feature: 'appreciate' },
                    { path: '/askfrank', label: 'AskFrank', hardLink: true, feature: 'askfrank' },
                ]
            },
            {
                key: 'games',
                label: 'games',
                icon: `${process.env.PUBLIC_URL}/img/icons/icon_202.png`,
                section: 'games',
                children: [
                    { path: '/games/lobbyburst', label: 'Lobby Burst!', feature: 'lobbyburst' },
                    { path: '/games/slimeburgerspin', label: 'Slimeburger Spin!', feature: 'slimespins' },
                ]
            },
            {
                key: 'franklyorigins',
                label: 'frankly',
                icon: `${process.env.PUBLIC_URL}/img/box.png`,
                section: 'franklyorigins',
                children: [
                    { path: '/about', label: 'About' },
                    { path: 'https://discord.gg/vehU36G44N', label: 'Discord', external: true },
                ]
            },
        ];

        if (userData) {
            items.push({
                key: 'members',
                label: 'members',
                icon: `${process.env.PUBLIC_URL}/img/icons/icon_203.png`,
                section: 'members',
                children: [
                    { path: '/members/profile', label: 'My Profile' },
                    { path: '/members/topposts', label: 'Community Posts', feature: 'topposts' },
                    { path: '/members/frampshack', label: "Frank's Framp Shack!", feature: 'frampshack' },
                    { path: '/frankopop', label: 'FrankoPop', feature: 'frankopop' },
                ]
            });
        }

        return items.map(item => {
            if (!item.children?.length) return item;

            const children = item.children
                .map(child => {
                    if (!child.feature || featureFlags[child.feature]) return child;
                    return isAdmin ? { ...child, disabled: true } : null;
                })
                .filter(Boolean);

            return { ...item, children };
        });
    }, [userData, featureFlags, isAdmin]);

    const navStructure = useMemo(() => {
        const map = { home: [] };
        navItems.forEach(item => {
            if (item.section) map[item.section] = item.children || [];
            if (item.url && item.key === 'home') map.home = [];
        });
        return map;
    }, [navItems]);

    const sectionFromPath = useCallback((pathname) => {
        for (const item of navItems) {
            if (!item.section) continue;
            if (item.children?.some(c => !c.external && (pathname === c.path || pathname.startsWith(c.path + '/')))) {
                return item.section;
            }
        }
        if (pathname.startsWith('/articles')) return 'news';
        if (pathname.startsWith('/members') || pathname.startsWith('/messages')) return 'members';
        if (pathname.startsWith('/staff') || pathname.startsWith('/housekeeping')) return 'staff';
        if (pathname.startsWith('/goodies')) return 'goodies';
        if (pathname.startsWith('/games')) return 'games';
        if (pathname.startsWith('/frankopop')) return 'members';
        if (pathname === '/about') return 'franklyorigins';
        return 'home';
    }, [navItems]);

    useEffect(() => {
        setActiveSection(sectionFromPath(location.pathname));
    }, [location.pathname, sectionFromPath]);

    const currentLinks = navStructure[activeSection] || [];

    const toggleMenu = () => setIsMenuOpen(v => !v);
    const closeMenu = () => setIsMenuOpen(false);

    const handleLoginClick = (e) => {
        if (e) e.preventDefault();
        setIsLoginModalOpen(true);
    };

    const handleLoginConfirm = () => {
        setIsLoginModalOpen(false);
        let apiUrl = process.env.REACT_APP_API_URL;
        if (apiUrl.endsWith('/api/')) apiUrl = apiUrl.slice(0, -5);
        else if (apiUrl.endsWith('/api')) apiUrl = apiUrl.slice(0, -4);
        window.location.href = `${apiUrl}/api/auth/discord`;
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour >= 0 && hour < 12) return 'Good morning';
        if (hour >= 12 && hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    const handleNavClick = (item) => {
        if (item.url) {
            navigate(item.url);
            if (item.label === 'home') setActiveSection('home');
            return;
        }
        if (item.section) setActiveSection(item.section);
    };

    const mailIcon = unreadCount > 0 ? 'mail_2.gif' : 'mail_1.gif';

    const mobileMenuPortal = isMenuOpen
        ? ReactDOM.createPortal(
            <div
                className={`lg:hidden fixed inset-0 bg-black bg-opacity-50 transition-opacity duration-300 ${
                    isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                style={{ zIndex: 999999 }}
                onClick={closeMenu}
            >
                <div
                    className={`absolute top-0 left-0 w-64 h-full bg-coal shadow-lg transition-transform duration-300 overflow-y-auto ${
                        isMenuOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                    style={{ zIndex: 999999 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-6">
                        <button onClick={closeMenu} className="absolute top-4 right-4 text-white text-2xl">
                            ×
                        </button>

                        <div className="mt-8 space-y-4">
                            {navItems.map((item, index) => {
                                const isActive = item.url
                                    ? (location.pathname === item.url && activeSection === 'home')
                                    : activeSection === item.section;

                                const subLinks = item.section ? navStructure[item.section] : [];

                                return (
                                    <div key={index}>
                                        <button
                                            onClick={() => {
                                                handleNavClick(item);
                                                if (item.url) closeMenu();
                                            }}
                                            className={`flex items-center gap-2 w-full text-white font-['Jersey_10'] uppercase text-[17px] tracking-[1px] py-2 text-left ${
                                                isActive ? 'bg-black bg-opacity-30 px-2 rounded' : ''
                                            }`}
                                            style={{ textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5), -1px 2px 0 rgba(0, 0, 0, 0.3)' }}
                                        >
                                            {item.icon && <img src={item.icon} alt="" className="mb-0.5" />}
                                            {item.label}
                                        </button>

                                        {subLinks && subLinks.length > 0 && isActive && (
                                            <div className="ml-4 mt-2 space-y-2 pb-2 border-b border-gray-700">
                                                {item.key === 'members' && userData && (
                                                    <div ref={userSearchRef} className="relative mb-2">
                                                        <input
                                                            type="text"
                                                            value={userSearchQuery}
                                                            onChange={handleUserSearch}
                                                            placeholder="Search members..."
                                                            className="font-['Jersey_10'] text-[15px] tracking-[1px] text-white placeholder-white/40 bg-black/30 border border-white/15 rounded px-3 py-1.5 outline-none focus:border-white/35 transition-colors w-full"
                                                            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                                                        />
                                                        {(userSearchLoading || userSearchQuery.trim().length >= 3) && (
                                                            <div
                                                                className="absolute left-0 right-0 mt-1 bg-coal rounded shadow-lg overflow-hidden"
                                                                style={{
                                                                    zIndex: 999999,
                                                                    backgroundImage: `url(${process.env.PUBLIC_URL}/img/console-bg-darker.png)`,
                                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
                                                                    border: '1px solid rgba(0,0,0,0.3)'
                                                                }}
                                                            >
                                                                {userSearchLoading && (
                                                                    <div className="px-3 py-2.5">
                                                                        <span className="font-['Jersey_10'] text-[15px] text-white/50 tracking-[1px]">Searching...</span>
                                                                    </div>
                                                                )}
                                                                {!userSearchLoading && userSearchResults.length === 0 && (
                                                                    <div className="px-3 py-2.5">
                                                                        <span className="font-['Jersey_10'] text-[15px] text-white/50 tracking-[1px]">No members found</span>
                                                                    </div>
                                                                )}
                                                                {!userSearchLoading && userSearchResults.map((user) => (
                                                                    <button
                                                                        key={user.userId}
                                                                        onClick={() => { handleUserSearchSelect(user.habboName); closeMenu(); }}
                                                                        className="w-full text-left px-3 py-2 hover:bg-black/30 transition-colors border-t border-black/20 first:border-t-0 block"
                                                                    >
                                                                        <span className="font-['Jersey_10'] text-[16px] text-white tracking-[1px]" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                                            {user.habboName}
                                                                        </span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {subLinks.map((subLink, subIndex) => {
                                                    const isSubActive = location.pathname === subLink.path;

                                                    if (subLink.external) {
                                                        return (
                                                            <a
                                                                key={subIndex}
                                                                href={subLink.path}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={closeMenu}
                                                                className="block text-white font-['Jersey_10'] uppercase text-[14px] tracking-[1px] py-1.5 px-2 rounded hover:bg-black hover:bg-opacity-25 transition-all"
                                                                style={{ textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5)' }}
                                                            >
                                                                {subLink.label}
                                                            </a>
                                                        );
                                                    }

                                                    return (
                                                        <Link
                                                            key={subIndex}
                                                            to={subLink.path}
                                                            reloadDocument={!!subLink.hardLink}
                                                            onClick={closeMenu}
                                                            title={subLink.disabled ? `${subLink.label} is switched off, admins only` : undefined}
                                                            className={`block font-['Jersey_10'] uppercase text-[14px] tracking-[1px] py-1.5 px-2 rounded transition-all ${
                                                                isSubActive
                                                                    ? 'bg-black bg-opacity-40 text-white'
                                                                    : 'text-gray-300 hover:bg-black hover:bg-opacity-25 hover:text-white'
                                                            }`}
                                                            style={{
                                                                color: subLink.isAdmin ? '#ff4444' : undefined,
                                                                opacity: subLink.disabled ? 0.4 : undefined,
                                                                textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5)'
                                                            }}
                                                        >
                                                            {subLink.label}
                                                        </Link>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {userData ? (
                                <button
                                    onClick={async () => {
                                        try {
                                            const { logout } = await import('../services/api');
                                            await logout();
                                            window.location.reload();
                                        } catch (error) {
                                            console.error('Logout failed:', error);
                                        }
                                    }}
                                    className="flex items-center gap-2 w-full text-white font-['Jersey_10'] uppercase text-[17px] tracking-[1px] py-2 hover:opacity-90 transition-opacity mt-4 border-t border-gray-700 pt-4"
                                    style={{ textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5), -1px 2px 0 rgba(0, 0, 0, 0.3)' }}
                                >
                                    <img src={`${process.env.PUBLIC_URL}/img/passlock.gif`} alt="" className="mb-0.5 mr-1" />
                                    Logout
                                </button>
                            ) : (
                                <button
                                    onClick={() => { closeMenu(); handleLoginClick(); }}
                                    className="flex items-center gap-2 text-white font-['Jersey_10'] uppercase text-[17px] tracking-[1px] py-2 hover:opacity-90 transition-opacity mt-4 border-t border-gray-700 pt-4"
                                    style={{ textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5), -1px 2px 0 rgba(0, 0, 0, 0.3)' }}
                                >
                                    <img src={`${process.env.PUBLIC_URL}/img/passlock.gif`} alt="" className="mb-0.5 mr-1" />
                                    login / register
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>,
            document.body
        )
        : null;

    return (
        <>
            <LoginModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onConfirm={handleLoginConfirm}
            />

            <nav
                className="w-full h-[55px] bg-coal relative"
                style={{
                    boxShadow: 'inset 0 3px 5px -3px rgba(0, 0, 0, 0.3), 0 3px 0 0 rgba(0, 0, 0, 0.15)',
                    backgroundImage: `url(${process.env.PUBLIC_URL}/img/console-bg-darker.png)`,
                }}
            >
                <div className="w-full max-w-[1300px] h-full mx-auto px-4 flex items-center justify-between">
                    <button
                        onClick={toggleMenu}
                        className="lg:hidden flex flex-col gap-1 w-6 h-6 justify-center items-center mt-1 hover:opacity-90 transition-opacity"
                        aria-label="Toggle menu"
                    >
                        <span className="w-5 h-0.5 bg-white"></span>
                        <span className="w-5 h-0.5 bg-white"></span>
                        <span className="w-5 h-0.5 bg-white"></span>
                    </button>

                    <div className="hidden lg:flex items-center">
                        {navItems.map((item, index) => {
                            const isActive = item.url
                                ? (location.pathname === item.url && activeSection === 'home')
                                : activeSection === item.section;

                            return (
                                <button
                                    key={index}
                                    onClick={() => handleNavClick(item)}
                                    className={`flex shrink-0 items-center gap-2 text-white font-['Jersey_10'] uppercase text-[17px] tracking-[1px] mt-1 mr-3 px-3 py-1.5 rounded transition-all ${
                                        isActive
                                            ? 'bg-black bg-opacity-40 lg:-translate-y-px'
                                            : 'lg:hover:bg-black lg:hover:bg-opacity-25 lg:hover:-translate-y-px'
                                    }`}
                                    style={{ textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5), -1px 2px 0 rgba(0, 0, 0, 0.3)' }}
                                >
                                    {item.icon && <img src={item.icon} alt="" className="mb-0.5" />}
                                    {item.label}
                                </button>
                            );
                        })}
                    </div>

                    {userData ? (
                        <div className="flex items-center gap-3">
                            <div ref={userMenuRef} className="relative">
                                <button
                                    onClick={() => {
                                        if (userMenuOpen) {
                                            setUserMenuOpen(false);
                                        } else {
                                            setUserMenuOpen(true);
                                        }
                                    }}
                                    className="flex items-center gap-2 text-white font-['Jersey_10'] uppercase text-lg tracking-[1px] px-3 py-1.5 rounded transition-all hover:bg-black/25"
                                    style={{
                                        border: '1px solid rgba(255,255,255,0.25)',
                                        textShadow: '-1px -1px 0 rgba(0,0,0,0.5), 1px -1px 0 rgba(0,0,0,0.5), -1px 1px 0 rgba(0,0,0,0.5), 1px 1px 0 rgba(0,0,0,0.5), -1px 2px 0 rgba(0,0,0,0.3)'
                                    }}
                                >
                                    {getGreeting()}, {userData.habboName}
                                    <div className="relative flex items-center">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/img/${mailIcon}`}
                                            alt="Messages"
                                        />
                                        {unreadCount > 0 && (
                                            <span
                                                className="absolute -top-1 -right-2 bg-red-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center"
                                                style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                                            >
                                                {unreadCount > 9 ? '9+' : unreadCount}
                                            </span>
                                        )}
                                    </div>
                                </button>
                                {userMenuOpen && (
                                    <div
                                        className="absolute right-0 mt-2 bg-coal rounded shadow-lg"
                                        style={{
                                            minWidth: 220,
                                            zIndex: 99999,
                                            backgroundImage: `url(${process.env.PUBLIC_URL}/img/console-bg-darker.png)`,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
                                            border: '1px solid rgba(0,0,0,0.3)'
                                        }}
                                    >
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate('/members/profile'); }}
                                            className="w-full text-left px-3 py-2.5 hover:bg-black/30 transition-colors"
                                        >
                                            <span className="font-['Jersey_10'] text-xl text-white flex items-center gap-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                                                </svg>
                                                My Profile
                                            </span>
                                        </button>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate('/members/settings/inventory'); }}
                                            className="w-full text-left px-3 py-2.5 hover:bg-black/30 transition-colors border-t border-black/20"
                                        >
                                        <span className="font-['Jersey_10'] text-xl text-white flex items-center gap-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
                                            </svg>
                                            My Inventory
                                        </span>
                                        </button>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate('/messages'); }}
                                            className="w-full text-left px-3 py-2.5 hover:bg-black/30 transition-colors border-t border-black/20"
                                        >
                                            <span className="font-['Jersey_10'] text-xl text-white flex items-center gap-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
                                                </svg>
                                                Inbox
                                                {unreadCount > 0 && (
                                                    <span className="bg-red-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                        {unreadCount > 9 ? '9+' : unreadCount}
                                                    </span>
                                                )}
                                                {unreadCount === 0 && <span className="text-white/50 text-md">(0)</span>}
                                            </span>
                                        </button>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate('/members/settings'); }}
                                            className="w-full text-left px-3 py-2.5 hover:bg-black/30 transition-colors border-t border-black/20"
                                        >
                                        <span className="font-['Jersey_10'] text-xl text-white flex items-center gap-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                            </svg>
                                            Settings
                                        </span>
                                        </button>
                                        <div className="bg-maroon h-2"></div>
                                        <button
                                            onClick={() => { setUserMenuOpen(false); navigate('/members/topposts'); }}
                                            className="w-full text-left px-3 py-2.5 hover:bg-black/30 transition-colors border-t border-black/20"
                                        >
                                            <span className="font-['Jersey_10'] text-xl text-white flex items-center gap-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                </svg>
                                                Community Posts
                                            </span>
                                        </button>
                                        {userData?.hasStaffRole && (
                                            <>
                                                <div className="bg-maroon h-2"></div>
                                                <a
                                                    href="/staff"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => setUserMenuOpen(false)}
                                                    className="block w-full text-left px-3 py-2.5 hover:bg-black/30 transition-colors border-t border-black/20"
                                                >
                                                    <span className="font-['Jersey_10'] text-xl text-white flex items-center gap-2" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M12 2a10 10 0 1 0 10 10H12V2z"/><circle cx="12" cy="12" r="2"/>
                                                        </svg>
                                                        Staff Panel
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-auto opacity-70">
                                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                                                        </svg>
                                                    </span>
                                                </a>
                                                <a
                                                    href="/staff/proimager"
                                                    onClick={() => setUserMenuOpen(false)}
                                                    className="block w-full text-left px-3 py-2.5 hover:bg-black/30 transition-colors border-t border-black/20"
                                                    style={{ backgroundColor: 'rgba(180, 140, 20, 0.15)' }}
                                                >
                                                    <span className="font-['Jersey_10'] text-lg block" style={{ color: '#d4af37', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                        Staff ProImager
                                                    </span>
                                                </a>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={handleLoginClick}
                            className="flex items-center gap-2 text-white font-['Jersey_10'] uppercase text-[17px] tracking-[1px] mt-1 px-3 py-1.5 rounded hover:bg-black hover:bg-opacity-25 transition-all"
                            style={{ textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5), -1px 2px 0 rgba(0, 0, 0, 0.3)' }}
                        >
                            <img src={`${process.env.PUBLIC_URL}/img/passlock.gif`} alt="" className="mb-0.5 mr-1" />
                            login / register
                        </button>
                    )}
                </div>

                {mobileMenuPortal}
            </nav>

            <div className="w-full hidden lg:flex justify-center relative">
                <div
                    className="w-full md:w-[1400px] bg-coal px-6 py-3 flex justify-between items-center"
                    style={{
                        borderBottomLeftRadius: '8px',
                        borderBottomRightRadius: '8px',
                        boxShadow: '0 3px 0 0 rgba(0, 0, 0, 0.15)',
                        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.0.5)), url(${process.env.PUBLIC_URL}/img/console-bg-darker.png)`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'repeat'
                    }}
                >
                    <div className="flex gap-6 flex-wrap">
                        {currentLinks.map((link, index) => {
                            const isActive = location.pathname === link.path;

                            if (link.external) {
                                return (
                                    <a
                                        key={index}
                                        href={link.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-1 rounded transition-all text-white hover:bg-black hover:bg-opacity-25"
                                        style={{ textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5)' }}
                                    >
                                        {link.label}
                                    </a>
                                );
                            }

                            return (
                                <Link
                                    key={index}
                                    to={link.path}
                                    reloadDocument={!!link.hardLink}
                                    onClick={() => setActivePath && setActivePath(link.path)}
                                    title={link.disabled ? `${link.label} is switched off, admins only` : undefined}
                                    className={`font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-1 rounded transition-all ${
                                        isActive ? 'bg-black bg-opacity-40' : 'hover:bg-black hover:bg-opacity-25'
                                    }`}
                                    style={{
                                        color: link.isAdmin ? '#ff4444' : link.isGold ? '#d4af37' : 'white',
                                        backgroundColor: link.isGold && !isActive ? 'rgba(180, 140, 20, 0.15)' : undefined,
                                        opacity: link.disabled ? 0.4 : undefined,
                                        textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5)'
                                    }}
                                >
                                    {link.label}
                                </Link>
                            );
                        })}
                    </div>

                    <div className="flex items-center gap-3">
                        {activeSection === 'members' && userData && (
                            <div ref={userSearchRef} className="relative">
                                <input
                                    type="text"
                                    value={userSearchQuery}
                                    onChange={handleUserSearch}
                                    placeholder="Search members..."
                                    className="font-['Jersey_10'] text-[15px] tracking-[1px] text-white placeholder-white/40 bg-black/30 border border-white/15 rounded px-3 py-1 outline-none focus:border-white/35 transition-colors w-44"
                                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                                />
                                {(userSearchLoading || userSearchQuery.trim().length >= 3) && (
                                    <div
                                        className="absolute right-0 mt-1 bg-coal rounded shadow-lg overflow-hidden"
                                        style={{
                                            minWidth: 176,
                                            zIndex: 99999,
                                            backgroundImage: `url(${process.env.PUBLIC_URL}/img/console-bg-darker.png)`,
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
                                            border: '1px solid rgba(0,0,0,0.3)'
                                        }}
                                    >
                                        {userSearchLoading && (
                                            <div className="px-3 py-2.5">
                                                <span className="font-['Jersey_10'] text-[15px] text-white/50 tracking-[1px]">Searching...</span>
                                            </div>
                                        )}
                                        {!userSearchLoading && userSearchResults.length === 0 && (
                                            <div className="px-3 py-2.5">
                                                <span className="font-['Jersey_10'] text-[15px] text-white/50 tracking-[1px]">No members found</span>
                                            </div>
                                        )}
                                        {!userSearchLoading && userSearchResults.map((user) => (
                                            <button
                                                key={user.userId}
                                                onClick={() => handleUserSearchSelect(user.habboName)}
                                                className="w-full text-left px-3 py-2 hover:bg-black/30 transition-colors border-t border-black/20 first:border-t-0"
                                            >
                            <span
                                className="font-['Jersey_10'] text-[16px] text-white tracking-[1px]"
                                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                            >
                                {user.habboName}
                            </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Font scale controls — desktop only, right of members search, left of logout */}
                        <div className="flex items-center gap-1.5 border border-white/15 rounded px-2 py-1 bg-black/20">
                            <button
                                type="button"
                                onClick={onFontScaleDown}
                                disabled={fontScale <= fontScaleMin}
                                className="flex items-center justify-center w-7 h-7 rounded hover:bg-black/40 text-white/60 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                                aria-label="Decrease text size"
                                title="Decrease text size"
                            >
                                <span className="font-['Jersey_10'] text-[13px] leading-none select-none">A</span>
                            </button>
                            <span className="text-white/20 text-[10px] select-none">|</span>
                            <button
                                type="button"
                                onClick={onFontScaleUp}
                                disabled={fontScale >= fontScaleMax}
                                className="flex items-center justify-center w-7 h-7 rounded hover:bg-black/40 text-white/60 hover:text-white disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                                aria-label="Increase text size"
                                title="Increase text size"
                            >
                                <span className="font-['Jersey_10'] text-[20px] leading-none select-none">A</span>
                            </button>
                        </div>
                        {userData && (
                            <button
                                onClick={async () => {
                                    try {
                                        const { logout } = await import('../services/api');
                                        await logout();
                                        window.location.reload();
                                    } catch (error) {
                                        console.error('Logout failed:', error);
                                    }
                                }}
                                className="flex items-center gap-2 text-white font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-3 py-1 rounded transition-all hover:bg-black hover:bg-opacity-25"
                                style={{ textShadow: '-1px -1px 0 rgba(0, 0, 0, 0.5), 1px -1px 0 rgba(0, 0, 0, 0.5), -1px 1px 0 rgba(0, 0, 0, 0.5), 1px 1px 0 rgba(0, 0, 0, 0.5)' }}
                            >
                                <img
                                    src={`${process.env.PUBLIC_URL}/img/passlock.gif`}
                                    alt=""
                                    className="w-4 h-4"
                                    style={{ imageRendering: 'pixelated' }}
                                />
                                Logout
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}

export default FullNavigator;